import {
  GraphQLServerUrl,
  RxDatabase,
  RxReplicationWriteToMasterRow,
  WithDeleted,
} from 'rxdb'
import { replicateGraphQL } from 'rxdb/plugins/replication-graphql'

import { graphql } from '@/gql'
import {
  createQueryWithHash,
  pullResponseModifier,
} from '@/system/rxdb/replications/utils.ts'
import { AssessmentSignature } from '@/system/rxdb/schemas/assessmentSignature.ts'

type PullData =
  | {
      nodes: Array<WithDeleted<AssessmentSignature>>
    }
  | { assessmentSignature: WithDeleted<AssessmentSignature> }

// amount of items we want to pull max for each iteration
const BATCH_SIZE = 20

export const SIGNATURE_FRAGMENT = /* GraphQL*/ `
    fragment assessmentSignature on AssessmentSignature {
        rowId
        assessmentId
        signatureConfirmPartnerMediation
        signatureConfirmProtocol
        signatureSection37CareRecipient
        signatureSection37Counselor
        signatureSection37Caregiver
        signatureSection45BillingPerson
        signatureSection45Caregiver
        updatedAt
        deleted
    }
`

export const createSignatureReplication = (
  db: RxDatabase,
  url: GraphQLServerUrl,
  autoStart: boolean
) => {
  // TODO: I believe we might not need streaming here
  const streamQuery = createQueryWithHash(
    graphql(/* GraphQL */ `
      subscription streamAssessmentSignatures {
        streamAssessmentSignatures {
          assessmentSignature {
            ...assessmentSignature
          }
        }
      }
    `)
  )

  const pullQuery = createQueryWithHash(
    graphql(/* GraphQL */ `
      query assessmentSignatureDocuments(
        $checkpoint: DocumentCheckpointInput
        $first: Int!
      ) {
        assessmentSignatureDocuments(checkpoint: $checkpoint, first: $first) {
          nodes {
            ...assessmentSignature
          }
        }
      }
    `)
  )

  const pushQuery = createQueryWithHash(
    graphql(/* GraphQL*/ `
      mutation assessmentSignatureReplicate(
        $rows: [AssessmentSignatureInput]!
      ) {
        assessmentSignatureReplicate(input: { items: $rows }) {
          result {
            ...assessmentSignature
          }
        }
      }
    `)
  )
  return replicateGraphQL({
    collection: db.assessment_signature,
    url,
    credentials: 'include',
    replicationIdentifier: url.http || '',
    autoStart,
    deletedField: 'deleted',
    pull: {
      includeWsHeaders: true,
      streamQueryBuilder: () => {
        return {
          ...streamQuery,
          variables: {},
        }
      },
      responseModifier: (data: PullData, origin) => {
        if (origin === 'stream' && data && 'assessmentSignature' in data) {
          return pullResponseModifier({ nodes: [data.assessmentSignature] })
        }
        return pullResponseModifier<AssessmentSignature>(
          data as {
            nodes: Array<WithDeleted<AssessmentSignature>>
          }
        )
      },
      batchSize: BATCH_SIZE,
      queryBuilder: (checkpoint, limit) => {
        return {
          ...pullQuery,
          variables: {
            checkpoint,
            first: limit,
          },
        }
      },
    },
    push: {
      responseModifier: (data: { result: AssessmentSignature[] }) => {
        return data.result
      },
      queryBuilder(rows: RxReplicationWriteToMasterRow<AssessmentSignature>[]) {
        return {
          ...pushQuery,
          variables: {
            rows: rows.map((row) => row.newDocumentState),
          },
        }
      },
    },
  })
}
